import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { fetchTeamMemberLookup } from '../../lib/entity-api';
import { useUser } from '../../contexts/UserContext';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { colors } from '../../design-system/colors';
import { useAnchoredList } from '../../hooks/useAnchoredList';

/**
 * Generic "pick a person of the company, or create one" selector: a search-as-you-type combobox over the company's
 * active people with the given roles (`user_company_relation.role`), plus a create option that lets the caller open
 * its own creation modal and refresh the list afterwards without remounting this component (`refreshToken`).
 *
 * The ONE implementation behind every such field: the Client/Lead selector (ClientLeadCombobox -> roles ['client'],
 * used by New Project and Proposals) and the Vendor selector (Bids) are this component with different props. Do not
 * copy its logic into a module-specific file.
 *
 * Typing free text is allowed by default (the selection then has no id): right for a field that stores a NAME (a bid's
 * subcontractor, an invoice payment's client). A field that stores only a REFERENCE (a bill's vendor, a bill payment's
 * vendor) passes `allowFreeText={false}`: typing only searches, the value changes when someone is picked (or the box is
 * emptied), and leaving the box puts the picked name back -- a name that points at nobody can never be left behind.
 * Keyboard: ArrowUp/ArrowDown move through the list, Enter selects the highlighted option, Escape closes.
 */
export interface PersonOption {
  id: string;
  name: string;
  email?: string;
}

export interface PersonSelection {
  /** null when the user typed free text instead of picking someone */
  id: string | null;
  name: string;
}

export interface PersonComboboxProps {
  roles: string[];
  value: string;
  onChange: (selection: PersonSelection) => void;
  placeholder?: string;
  className?: string;
  onCreateNew?: () => void;
  /** Label of the create option (default "Add new"). */
  createLabel?: string;
  /** Bump this (e.g. after creating someone elsewhere) to force an immediate refetch of the list without remounting. */
  refreshToken?: number;
  disabled?: boolean;
  id?: string;
  /** false: typing only searches; only picking a person (or emptying the box) changes the value. Default true. */
  allowFreeText?: boolean;
  /** The gray "Select" option on top of the list: picking it leaves the field empty (null). Default true; never shown while the user is typing a search. */
  clearable?: boolean;
}

const displayName = (person: { company_name?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null }) =>
  person.company_name || `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || 'Unknown';

export function PersonCombobox({
  roles,
  value,
  onChange,
  placeholder = 'Select',
  className = '',
  onCreateNew,
  createLabel = 'Add new',
  refreshToken,
  disabled,
  id,
  allowFreeText = true,
  clearable = true,
}: PersonComboboxProps) {
  const { companyId } = useUser();
  const [options, setOptions] = useState<PersonOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  // the list narrows only while the user is typing: opening a box that already has someone shows everyone
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The list is portaled to <body> (hooks/useAnchoredList.ts): inside a modal's scrolling body it used to be clipped and hidden behind the footer.
  const { anchorRef, listRef, listStyle, isInsideList } = useAnchoredList(isOpen);
  const rolesKey = roles.join(',');
  const listboxId = useId();

  // Fetch the people when the component mounts, the company changes, or the caller bumps refreshToken.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setIsLoading(true);
    fetchTeamMemberLookup(rolesKey.split(','))
      .then((people) => {
        if (!cancelled) setOptions(people.map((person) => ({ id: person.id, name: displayName(person), email: person.email })));
      })
      .catch((error) => console.error('Error fetching people for the selector:', error))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshToken, rolesKey]);

  const filteredOptions = useMemo(() => {
    const searchTerm = isSearching ? inputValue.trim().toLowerCase() : '';
    if (!searchTerm) return options;
    return options.filter((option) => option.name.toLowerCase().includes(searchTerm) || (option.email && option.email.toLowerCase().includes(searchTerm)));
  }, [inputValue, options, isSearching]);

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    setHighlighted(0);
  }, [inputValue, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && !isInsideList(target)) {
        setIsOpen(false);
        setIsSearching(false);
        if (!allowFreeText) setInputValue(value); // leaving without picking: back to who is actually selected
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowFreeText, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsSearching(true);
    // searching only (allowFreeText false): the selection changes when someone is picked, or when the box is emptied
    if (allowFreeText || e.target.value === '') onChange({ id: null, name: e.target.value });
    setIsOpen(true);
  };

  const handleSelectOption = (option: PersonOption) => {
    setInputValue(option.name);
    setIsSearching(false);
    onChange({ id: option.id, name: option.name });
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setInputValue('');
    setIsSearching(false);
    onChange({ id: null, name: '' });
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setIsSearching(false);
      if (!allowFreeText) setInputValue(value);
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      e.preventDefault();
      setIsOpen(true);
      setHighlighted((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp' && filteredOptions.length > 0) {
      e.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (e.key === 'Enter' && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelectOption(filteredOptions[Math.min(highlighted, filteredOptions.length - 1)]);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative" ref={anchorRef}>
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={inputValue}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          className={`flex h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 font-form-input generic-form-field pr-8 ${isOpen ? 'ring-1 ring-ring border-ring' : 'border-gray-300'}`}
        />
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
      </div>

      {isOpen && listStyle && createPortal(
        <div
          ref={listRef}
          className="fixed z-[var(--z-overlay)] bg-white border-2 border-border rounded-lg shadow-lg max-h-60 overflow-auto"
          style={listStyle}
        >
          {isLoading ? (
            <div className="p-2 text-sm text-gray-500">Loading...</div>
          ) : (
            <ul id={listboxId} className="py-1" role="listbox">
              {clearable && !isSearching && (
                <li role="option" aria-selected={!inputValue} onClick={handleClear} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-400">
                  Select
                </li>
              )}
              {filteredOptions.map((option, index) => (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={inputValue === option.name}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${index === highlighted ? 'bg-gray-100' : ''}`}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.name}</div>
                    {option.email && <div className="text-xs text-gray-500">{option.email}</div>}
                  </div>
                  {inputValue === option.name && <Check className="h-4 w-4 text-gray-600" />}
                </li>
              ))}
              {filteredOptions.length === 0 && (
                <div className="p-2 text-sm" style={{ color: colors.dropdownMutedText }}>
                  {inputValue.trim() ? 'No matching results' : 'No results found'}
                </div>
              )}
              {onCreateNew && (
                <li
                  onClick={() => {
                    setIsOpen(false);
                    onCreateNew();
                  }}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 border-t-2 border-border"
                  style={{ color: colors.dropdownText }}
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm font-medium">{createLabel}</span>
                </li>
              )}
            </ul>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
